<template>
    <div>
        <b-modal
            v-model="modalShowing"
            centered
            size="md"
            title="Create a new..."
            hide-footer
            body-class="bg-gray-100"
            no-close-on-backdrop
            @hidden="onModalHidden"
            @show="onModalShow"
        >
            <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
            
            <div class="min-height-300 d-flex align-items-center justify-content-center">
            <transition name="fade-fast" mode="out-in">
            
                <div v-if="mode == 'picker'" key="picker" class="h-100 w-100 d-flex justify-content-center align-items-center">
                    <LoadingContext :loading="loadingNodes" class="h-100 w-100">
                        <div class="h-100 d-flex gap-4 align-items-stretch flex-wrap justify-content-around">
                            <div v-if="noStartNodesLoaded" class="font-italic">
                                No options available based on your role.
                            </div>
                            <button
                                v-for="node in startNodes"
                                :key="node.key"
                                class="p-4 mt-3 border rounded-lg overflow-hidden flow-object-starter-button"
                                @click="handleNodeClicked(node)"
                            >
                                <div class="h5">{{ node.title }}</div>
                                <div class="button-description-text">
                                    {{ node.description }}
                                </div>
                            </button>
                        </div>
                    </LoadingContext>
                </div>

                <div v-else-if="mode == 'form'" key="form" class="h-100 w-100">
                    <LoadingContext :loading="loadingForm" class="d-flex flex-column h-100">
                        <!-- DO NOT HIDE FORM DURING SAVE - we use form error slots to show errors - and it must exist in DOM when we call method to do that -->
                        <h5 v-if="formTitle" class="text-primary-darker">{{formTitle}}</h5>
                        <p v-if="formInstructions">{{formInstructions}}</p>
                        <FormulateForm
                            ref="startForm"
                            name="startForm"
                            :schema="formSchema"
                            v-model="formData"
                            class="mt-3"
                            @submit="submitForm"
                            #default="{ isLoading, hasErrors }"
                        >
                            <div class="mt-3 d-flex justify-content-between align-items-end gap-3">
                                <div class="">
                                    <b-link @click="handleBack">&larr; Back</b-link>
                                </div>
                                <div class="d-flex gap-3">
                                    <b-button
                                        variant="gray-dark"
                                        size="lg"
                                        :disabled="isLoading || savingForm"
                                        @click="handleCancel"
                                    >
                                        Cancel 
                                    </b-button>
                                    <b-button
                                        variant="primary"
                                        class=""
                                        size="lg"
                                        :disabled="hasErrors || isLoading || savingForm"
                                        @click="startSubmitWithSave"
                                    >
                                        <b-spinner v-if="savingForm" small></b-spinner>
                                        {{ ( isLoading || savingForm ) ? '' : 'Submit' }}
                                    </b-button>
                                </div>
                            </div>
                        </FormulateForm>
                    </LoadingContext>
                </div>

                <div v-else-if="mode == 'success'" key="success" class="h-100 d-flex align-items-center justify-content-center">
                    <save-success message="Redirecting to next step..."></save-success>
                </div>

            </transition>
            </div>

        </b-modal>

    </div>
</template>

<script>
import { 
    getFlowObjectApi,
} from '../../js/api-service';
import { errorHandling } from '../../js/custom-vue-mixins';
import isObject from 'lodash/isObject';
import LoadingContext from '../shared/LoadingContext.vue';

export default {
    name: 'FlowObjectStarter',
    components: {
        LoadingContext,
    },
    mixins: [
        errorHandling,
    ],
    props: {
        flowObjectClass: {
            type: String,
            required: true,
        },
        flowSubtype: {
            type: String,
            default: null,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loadingNodes: false,
            startNodes: null,
            activeNode: null,  // the currently selected/active start_node
            loadingForm: false,
            savingForm: false,
            form: null,  // the ONE form allowed for the active start_node
            formData: null,
            withSave: false,  // toggle for doing a real save or a check save - no longer using?
            mode: 'picker',  // switch between 'picker', 'form', 'success'
            // MODAL
            modalShowing: false,
        }
    },
    computed: {
        formSchema() {
            return this.form?.items ?? [];
        },
        formTitle() {
            // the title to show above the start form, with instructions, if any -- from the active form
            return this.form?.title ?? '';
        },
        formInstructions() {
            // the instructions to show abvoe the start form, with title, if any -- from the active form
            return this.form?.instructions ?? '';
        },
        flowObjectApi() {
            return getFlowObjectApi(this.flowObjectClass, this.flowSubtype);
        },
        noStartNodesLoaded() {
            // not loading, but has array, and array is empty
            return !this.loadingNodes && Array.isArray(this.startNodes) && !this.startNodes.length;
        },
    },
    created() {
        //this.startNodes = [];  // start with empty array - moved to resetSelection
        this.resetSelection();  // sets defaults for all form things
    },
    mounted() {
        //this.getStartNodes();  // TODO - modify this to make API call on show() of modal!
    },
    methods: {
        // PUBLIC METHODS - allow this to be mounted, but modal not open 
        show() {
            // TODO - add ability to pass in the id/key of some node to start that one directly??? only if needed - see WorkflowStarter as a reference

            this.modalShowing = true;
        },
        hide() {
            this.handleCancel();  // same as hitting cancel button
        },

        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        
        handleNodeClicked(node) {
            this.activeNode = {...node};
            this.getForm(node.key);
            //this.modalShowing = true;
            this.mode = 'form';
        },
        onModalHidden() {  // modal is already closed here 
            this.resetSelection();
        },
        onModalShow() {
            this.getStartNodes();  // no need to await
        },
        resetSelection() {  // just reset things, but don't open/close modal 
            this.mode = 'picker';
            this.activeNode = {};
            this.form = {};
            this.formData = {};
            this.savingForm = false;
            this.startNodes = [];
        },
        handleCancel() {
            this.modalShowing = false;
            // then let @hidden call resetSelection() - via onModalHidden()
        },
        handleBack() {
            // the back button when selecting a node, then changing mind and going back to select another
            this.getStartNodes();  // refresh these
            this.resetSelection();
        },
        
        // API METHODS
        async getStartNodes() {
            this.loadingNodes = true;
            let resp;
            try {
                resp = await this.flowObjectApi.get_start_nodes();
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp?.data) {
                this.startNodes = resp.data;
            }
            this.loadingNodes = false;
        },
        async getForm(nodeKey) {
            this.loadingForm = true;
            let resp;
            const axiosConfig = {
                params: {
                    node_key: nodeKey,
                }
            };
            try {
                resp = await this.flowObjectApi.get_start_node_form(axiosConfig);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp?.data) {
                this.form = resp.data.form;  // only form object (with schema inside), no data in this call
            }
            this.loadingForm = false;
        },
        startSubmitWithSave() {
            this.withSave = true;
            this.$formulate.submit('startForm');  // start submit process
        },
        startSubmitNoSave() {
            this.withSave = false;
            this.$formulate.submit('startForm');
        },
        async submitForm(submitData) {
            this.savingForm = true;
            const postData = {
                node_key: this.activeNode.key,
                with_save: this.withSave,
                form_data: {...submitData},
            };
            // reset validation here -- this method won't even get called by formulate unless all FIELDS are valid
            // and we need to clear any errors we set from backend so we can allow user to try again
            this.$formulate.resetValidation('startForm');
            let resp;
            try {
                resp = await this.flowObjectApi.submit_start_node_form(postData);
            } catch (error) {
                //this.apiErrorHandler(error);
                // do specific error handling here
                // error.response.data will be a dict/object with keys being field names, values being an ARRAY of error messages for that field
                // but we might have keys that are not fields on the form 
                //      - either form-level errors, or 'resolved_input' that has errors (data we resolve from other submitted fields)
                //      - for either of those, we will show them in the 'non-field' errors section 
                if (error.response?.data && isObject(error.response.data)) {
                    console.log('error.response.data is');
                    console.log(error.response.data);
                    const fieldMap = this.$refs.startForm?.registry?.registry || new Map();  // inner registry is a Map()
                    //const fieldNames = fieldMap ? Array.from(fieldMap.keys()) : [];  // all field names on the form 
                    const inputErrors = {};  // formulate expects this for FIELD errors - object of fieldName: array of messages
                    const formErrors = [];  // formulate expects this for NON-FIELD errors - array of messages
                    Object.entries(error.response.data).forEach(([errKey, errMsgArray]) => {  // Object.entries --> first arg is the array of [key, val] - need to destructure it 
                        console.log('errKey + errMsgArray', errKey, errMsgArray);
                        if (fieldMap.has(errKey)) {
                            inputErrors[errKey] = [...errMsgArray];
                        } else {
                            console.log('typeof errMsgArray', typeof errMsgArray);
                            formErrors.push(...errMsgArray);
                        }
                    });
                    if (Object.entries(inputErrors).length || formErrors.length) {
                        this.$formulate.handle({inputErrors, formErrors}, 'startForm');  // see formulate docs for syntax of this...
                    }
                }
                this.savingForm = false;
            }
            // will have data and schema this time 
            if (resp) {
                // IF WE WERE SUCCESSFUL
                if (resp.data.redirect_url) {
                    this.mode = 'success';
                    setTimeout(() => {
                        window.location.replace(resp.data.redirect_url);
                    }, 1000);
                } else {
                    this.withSave = false;  // set back to false right away
                    this.form = resp.data.form;  // only form object (with schema inside), no data in this call
                    this.formData = resp.data.data;  // set it on instance... might come back with more if you didn't save it yet (maybe desired)
                    this.savingForm = false;
                }
            }
            
        },
    }
}
</script>

<style lang="scss" scoped>

button.flow-object-starter-button {
    background-color: var(--primary-100);
    box-shadow: $box-shadow-sm;
    transition: 0.2s ease-out all;
    width: 300px;
    // should really use css grid for these...

    .button-description-text {
        color: var(--gray-500);
    }

    &:hover {
        background-color: var(--primary-50);
        box-shadow: $box-shadow;

        .button-description-text {
            color: var(--gray-900);
        }
    }

}

</style>